import {Injectable} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
//import { ToasterService } from "angular2-toaster";
import {
    LogLevel,
    Multiselect,
    MultiselectItem,
    MultiselectItems,
    MultiselectSettings,
    ToastMessage
} from "../interfaces/admin.interface";
import {LoggingService} from "./log.service";
import {PortalService} from "./portal.service";
import {UtilsService} from "./utils.service";
import {ToastrService} from "ngx-toastr";

@Injectable({
    providedIn: 'root'
})
/** Method for multiselect components management centralization */
export class MultiselectService {

    companies: MultiselectItems = {
        company: []
    };

    constructor(
        private adminService: PortalService,
        private logService: LoggingService,
        private toasterService: ToastrService,
        private translateService: TranslateService,
        private utilsService: UtilsService
    ) { }

    /**
     * Method that loads the elements to be used in a angular2-multiselect component.
     *
     * (To be used in component ngOnInit())
     * @param multiselectItems - multiselectItems object created in page
     * @param multiselect - multiselect value depending on select type (es: Multiselect.JOB_ID)
     * @param toastMessage - toastMessage object defined in specific page / component
     * @param nameKeys - (OPTIONAL) array key strings trough which create a custom name for the multiselect entry
     * @param avKeys - (OPTIONAL) array key strings through with apply an additional value to the multiselect object
     */
    async loadMultiselectItems(
        multiselectItems: MultiselectItems,
        multiselect: Multiselect,
        toastMessage: ToastMessage,
        nameKeys?: string[],
        avKeys?: string[]
    ): Promise<void> {
        if(multiselect == Multiselect.COMPANY && this.companies.company.length > 0) {
            multiselectItems.company = [... this.companies.company]
            return
        }
        await this.loadItems(multiselectItems, multiselect, toastMessage, nameKeys, avKeys);
        if(multiselect == Multiselect.COMPANY) {
            this.companies.company = multiselectItems.company
        }
    }

    /**
     * Method that loads the elements to be used in a angular2-multiselect component from a list of objects returned from an /all API.
     *
     * (To be used in component ngOnInit())
     *
     * This function is required whenever the data to display in a multiselect are obtained from a classic service
     * and not from a specific multiselect API.
     * @param multiselectItems - multiselectItems object created in page
     * @param multiselect - multiselect value depending on select type (es: Multiselect.JOB_ID)
     * @param itemList - list of objects received from the API
     * @param nameKeys - (OPTIONAL) array key strings trough which create a custom name for the multiselect entry
     * @param avKeys - (OPTIONAL) array key strings through with apply an additional value to the multiselect object
     */
    loadMultiselectFromList(
        multiselectItems: MultiselectItems,
        multiselect: Multiselect,
        itemList: any[],
        nameKeys?: string[],
        avKeys?: string[]
    ): void {
        multiselectItems[multiselect] = this.getItemsFromData(itemList, nameKeys, avKeys);
    }

    /**
     * Method that configures a multiselect component as defined in its settings and retruns it as required.
     *
     * (To be used in setMultiselectSettings())
     * @param key -  multiselect value depending on select type (es: Multiselect.JOB_ID)
     * @param ref - multiselect specific object based on multiselectSettings_base
     * @param last
     * @param processing - processing object defined in page / component
     */
    applyFilterTranslationText(key: Multiselect, ref: MultiselectSettings, last: boolean = false, processing: any): MultiselectSettings {
        processing.translation = true;
        this.translateService.get("multiselect").subscribe(
            (d) => {
                ref.text = d[key];
                ref.selectAllText = d.selectAll;
                ref.unSelectAllText = d.unSelectAll;
                ref.searchPlaceholderText = d.search;

                if (last) { processing.translation = false; }
            }, () => {
                processing.translation = false;
                ref.text = key;
            }
        );
        return ref;
    }

    // ------------ PRIVATE METHODS ------------ //

    private async loadItems(
        multiselectItems: MultiselectItems,
        multiselectType: Multiselect,
        toastMessage: ToastMessage,
        nameKeys?: string[],
        avKeys?: string[]
    ): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.adminService.getMultiselectItems(multiselectType).subscribe(
                (response) => {
                    this.logService.log("[SUCCESS] Caricamento multiselect", response, LogLevel.DEBUG);
                    const { data } = response;
                    multiselectItems[multiselectType] = this.getItemsFromData(data, nameKeys, avKeys);
                    resolve();
                },
                (error) => {
                    this.logService.log("[ERROR] Caricamento multiselect", error, LogLevel.DEBUG);
                    this.toasterService.error(toastMessage.multiselect, this.utilsService.manageErrorMsg(error));
                    reject();
                }
            );
        });
    }

    /**
     * Method that creates the values to insert in a multiselect.
     *
     * Values can be standard id and name, or can be customized by passing the keys to get the
     * object properties that creates a custom name or additionalValues, keys has to be formatted the following way:
     *
     * 'string': for every key string the multiselect will show its value separated with a "-"
     * 'string+string': a key that has to be composed of two properties before being separated with a "-"
     */
    private getItemsFromData(data: any, nameKeys?: string[], avKeys?: string[]): MultiselectItem[] {
        return data.map((d: any) => {
            const val = {
                id: d.id,
                itemName: nameKeys ? this.generateObjectName(d, nameKeys) : d.name,
                ...(avKeys) && { additionalValues: this.generateAdditionalValues(d, avKeys) }
            };
            if(d['company'])
                val['company'] = d['company']
            return val;
        });
    }

    /** Method that returns a multiselect name composed of concatenated properties defined by their keys */
    private generateObjectName(d: any, nameKeys: string[]): string {
        const values: string[] = [];
        nameKeys.forEach((k: string, i: number) => {
            k.split('+').map((sk: string) => values[i] ? values[i] += ` ${d[sk]}` : values.push(d[sk]));
        });

        return values.join(' - ');
    }

    /** Method that creates an object of additionalValues using the given object keys */
    private generateAdditionalValues = (d: any, avKeys: string[]): any => {
        const value: any = {};
        avKeys.forEach((avk: string) => { value[avk] = d[avk]; });

        return value;
    }

}
